import React, { useEffect, useState, useMemo, useCallback } from "react";
import MessengerLayout from "../../layout/MessengerLayout";
import WorkforceWidget   from "./widgets/WorkforceWidget";
import PayrollWidget     from "./widgets/PayrollWidget";
import AttendanceWidget  from "./widgets/AttendanceWidget";
import LeaveWidget       from "./widgets/LeaveWidget";
import PerformanceWidget from "./widgets/PerformanceWidget";
import TicketsWidget     from "./widgets/TicketsWidget";
import ProjectsWidget    from "./widgets/ProjectsWidget";
import InsightsStripWidget from "./widgets/InsightsStripWidget";
import RiskSignalsPanel  from "./insights/RiskSignalsPanel";
import KiraInsightPanel  from "./insights/KiraInsightPanel";
import { ErrorState }    from "../../components/ui/LoadingStates";
import ErrorBoundary     from "../../components/ui/ErrorBoundary";
import { RefreshCw, Download, Brain, LayoutGrid, Maximize2 } from "lucide-react";
import RotaSchedulerPage from "../rota/RotaSchedulerPage";
import ExecLearningPage from "../learning/ExecLearningPage";

const API   = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "application/json" });

const PERIODS = [
  { label: "This Month",    value: "month"   },
  { label: "This Quarter",  value: "quarter" },
  { label: "This Year",     value: "year"    },
  { label: "Last 6 Months", value: "6m"      },
  { label: "Last 12 Months",value: "12m"     },
];

const NEON  = "#00ff90";
const card  = "rounded-2xl border border-[#00ff90]/20 bg-[#080808] shadow-[0_0_24px_#00ff9010]";

export default function ExecutiveDashboardPage() {
  const [period,    setPeriod]    = useState("month");
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [lastSync,  setLastSync]  = useState(null);
  const [kiraOpen,  setKiraOpen]  = useState(false);
  const [kiraCtx,   setKiraCtx]   = useState(null);
  const [fullWidget,setFullWidget]= useState(null);
  const [view,      setView]      = useState("dashboard"); // dashboard | rota

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetch(`${API}/api/executive/dashboard?period=${period}`, { headers: authH() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d   = await res.json();
      setData(d?.data || d || null);
      setLastSync(new Date());
    } catch (e) {
      console.error(e);
      setLoadError(e.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    load();
    // Executive dashboard refreshes on every K.I.R.A action.
    const handleKiraAction = () => load();
    window.addEventListener("kira_action_complete", handleKiraAction);
    return () => window.removeEventListener("kira_action_complete", handleKiraAction);
  }, [load]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  const openKira = (context, prompt) => {
    setKiraCtx({ context, prompt });
    setKiraOpen(true);
  };

  const exportDashboard = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = `executive_dashboard_${period}_${new Date().toISOString().slice(0,10)}.json`;
    a.click(); URL.revokeObjectURL(url);
  };

  const kpiCards = useMemo(() => {
    if (!data) return [];
    const w = data.workforce || {};
    const p = data.payroll   || {};
    const a = data.attendance|| {};
    const perf = data.performance || {};
    const t = data.tickets   || {};
    return [
      { label: "Headcount",       value: w.headcount ?? "—",             delta: w.headcount_growth_pct, unit: "",  color: NEON,     sub: "total employees" },
      { label: "Monthly Payroll", value: p.monthly_cost ? `£${(p.monthly_cost/1000).toFixed(0)}k` : "—", delta: p.mom_change_pct, unit: "", color: "#60a5fa", sub: "total cost" },
      { label: "Attendance Rate", value: a.today_rate ? `${a.today_rate}%` : "—", delta: a.mom_change, unit: "", color: "#34d399", sub: "today" },
      { label: "Reviews Done",    value: perf.review_completion ? `${perf.review_completion}%` : "—", delta: null, unit: "", color: "#fbbf24", sub: "this cycle" },
      { label: "Open P1 Tickets", value: t.open_p1 ?? "—",              delta: null, unit: "", color: t.open_p1 > 0 ? "#f87171" : "#34d399", sub: "critical" },
      { label: "Attrition Rate",  value: w.attrition_rate ? `${w.attrition_rate}%` : "—", delta: w.attrition_delta, unit: "", color: "#e879f9", sub: "rolling 12m" },
    ];
  }, [data]);

  return (
    <MessengerLayout>
      <div className="min-h-screen bg-[#050505] text-white p-6 space-y-6">

        {/* ── Header ── */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-2 h-2 rounded-full bg-[#00ff90] animate-pulse" />
              <span className="text-xs text-[#00ff90]/50 uppercase tracking-[0.2em] font-medium">Executive Intelligence</span>
            </div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Space Mono', monospace" }}>
              Workforce Dashboard
            </h1>
            {lastSync && (
              <p className="text-xs text-white/20 mt-1">
                Last updated: {lastSync.toLocaleTimeString()}
              </p>
            )}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Period selector */}
            <div className="flex gap-1 bg-black/60 border border-[#00ff90]/15 rounded-xl p-1">
              {PERIODS.map(p => (
                <button key={p.value} onClick={() => setPeriod(p.value)}
                  className={`text-xs px-3 py-1.5 rounded-lg transition-all ${
                    period === p.value
                      ? "bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30"
                      : "text-white/30 hover:text-white/60"
                  }`}>
                  {p.label}
                </button>
              ))}
            </div>

            <button onClick={load} disabled={loading}
              className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl border border-white/10 text-white/40 hover:text-white transition-colors">
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
            </button>

            <button onClick={exportDashboard}
              className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl border border-white/10 text-white/40 hover:text-white transition-colors">
              <Download size={13} /> Export
            </button>

            <button onClick={() => openKira(data, "Summarise the current state of the workforce and highlight the top 3 actions I should take today.")}
              className="flex items-center gap-2 text-xs px-4 py-2 rounded-xl border border-[#00ff90]/30 bg-[#00ff90]/8 text-[#00ff90] hover:bg-[#00ff90]/15 transition-colors font-medium">
              <Brain size={13} /> Ask KIRA
            </button>
          </div>
        </div>

        {/* ── View tabs ── */}
        <div className="flex gap-2 border-b border-white/5">
          {[["dashboard", "Dashboard"], ["rota", "Rota"], ["learning", "L&D"]].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)}
              className={`px-4 py-2 text-sm transition-colors -mb-px border-b-2 ${
                view === k ? "text-[#00ff90] border-[#00ff90]" : "text-white/50 border-transparent hover:text-white/80"
              }`}>
              {l}
            </button>
          ))}
        </div>

        {view === "rota" && (
          <div className="rounded-2xl border border-[#00ff90]/20 bg-[#080808] p-4">
            <p className="text-xs text-white/40 mb-2">Read-only org-wide rota overview.</p>
            <RotaSchedulerPage embedded={true} />
          </div>
        )}

        {view === "learning" && (
          <div className="rounded-2xl border border-[#00ff90]/20 bg-[#080808] p-4">
            <ExecLearningPage embedded={true} />
          </div>
        )}

        {view === "dashboard" && loadError && (
          <ErrorState
            message="Couldn't load executive dashboard"
            detail={loadError}
            onRetry={load}
          />
        )}

        {view === "dashboard" && !loadError && (
        <>
        {/* ── KPI Strip ── */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {loading && kpiCards.length === 0 &&
            Array.from({ length: 6 }).map((_, i) => (
              <div key={`kpi-sk-${i}`} className={`${card} p-4`} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div className="h-2 rounded bg-white/5 w-1/2" style={{
                  background: "linear-gradient(90deg, #111 25%, #1a1a1a 50%, #111 75%)",
                  backgroundSize: "200% 100%",
                  animation: "shimmer 1.5s linear infinite",
                }} />
                <div className="h-7 rounded bg-white/5 w-2/3" style={{
                  background: "linear-gradient(90deg, #111 25%, #1a1a1a 50%, #111 75%)",
                  backgroundSize: "200% 100%",
                  animation: "shimmer 1.5s linear infinite",
                }} />
                <div className="h-2 rounded bg-white/5 w-1/3" style={{
                  background: "linear-gradient(90deg, #111 25%, #1a1a1a 50%, #111 75%)",
                  backgroundSize: "200% 100%",
                  animation: "shimmer 1.5s linear infinite",
                }} />
              </div>
            ))}
          {kpiCards.map((k, i) => (
            <div key={i} className={`${card} p-4 relative overflow-hidden group cursor-pointer hover:border-[#00ff90]/40 transition-all`}
              onClick={() => openKira(data, `Analyse the "${k.label}" metric: current value is ${k.value}. Give context, trend analysis and recommended action.`)}>
              {/* Glow */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ background: `radial-gradient(ellipse at 50% 100%, ${k.color}08, transparent 70%)` }} />
              <p className="text-xs text-white/30 mb-2 uppercase tracking-wider">{k.label}</p>
              {loading ? (
                <div className="h-8 rounded bg-white/5 animate-pulse mb-1" />
              ) : (
                <p className="text-2xl font-bold font-mono" style={{ color: k.color }}>{k.value}</p>
              )}
              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-white/20">{k.sub}</p>
                {k.delta != null && (
                  <span className={`text-xs font-mono ${k.delta >= 0 ? "text-[#00ff90]/60" : "text-red-400/60"}`}>
                    {k.delta >= 0 ? "+" : ""}{k.delta?.toFixed(1)}%
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* ── Insights strip — DQ ring + anomalies + leave liability ── */}
        <InsightsStripWidget onAskKira={openKira} />

        {/* ── Risk Signals ── */}
        <RiskSignalsPanel data={data} onAskKira={openKira} />

        {/* ── Main Grid ── all widgets share the parent's fetched data,
              so they share one boundary. KPIs above, InsightsStrip, and
              Risk Signals stay outside. */}
        <ErrorBoundary variant="panel" label="Executive widgets">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
            <WorkforceWidget   data={data?.workforce}   period={period} loading={loading} onAskKira={openKira} onExpand={() => setFullWidget("workforce")}   />
            <PayrollWidget     data={data?.payroll}     period={period} loading={loading} onAskKira={openKira} onExpand={() => setFullWidget("payroll")}     />
            <AttendanceWidget  data={data?.attendance}  period={period} loading={loading} onAskKira={openKira} onExpand={() => setFullWidget("attendance")}  />
            <LeaveWidget       data={data?.leave}       period={period} loading={loading} onAskKira={openKira} onExpand={() => setFullWidget("leave")}       />
            <PerformanceWidget data={data?.performance} period={period} loading={loading} onAskKira={openKira} onExpand={() => setFullWidget("performance")} />
            <TicketsWidget     data={data?.tickets}     period={period} loading={loading} onAskKira={openKira} onExpand={() => setFullWidget("tickets")}     />
          </div>

          {/* Projects - full width */}
          <ProjectsWidget data={data?.projects} period={period} loading={loading} onAskKira={openKira} />
        </ErrorBoundary>
        </>
        )}

        {/* ── KIRA Panel ── owns its own fetch, isolated boundary */}
        {kiraOpen && (
          <ErrorBoundary variant="panel" label="KIRA insight">
            <KiraInsightPanel
              context={kiraCtx?.context}
              initialPrompt={kiraCtx?.prompt}
              onClose={() => setKiraOpen(false)}
            />
          </ErrorBoundary>
        )}

        {/* ── Expanded widget modal ── */}
        {fullWidget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <div className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl border border-[#00ff90]/20 bg-[#080808]">
              <button onClick={() => setFullWidget(null)}
                className="absolute top-4 right-4 text-white/30 hover:text-white transition-colors z-10">✕</button>
              <div className="p-6">
                {fullWidget === "workforce"   && <WorkforceWidget   data={data?.workforce}   period={period} loading={loading} onAskKira={openKira} expanded />}
                {fullWidget === "payroll"     && <PayrollWidget     data={data?.payroll}     period={period} loading={loading} onAskKira={openKira} expanded />}
                {fullWidget === "attendance"  && <AttendanceWidget  data={data?.attendance}  period={period} loading={loading} onAskKira={openKira} expanded />}
                {fullWidget === "leave"       && <LeaveWidget       data={data?.leave}       period={period} loading={loading} onAskKira={openKira} expanded />}
                {fullWidget === "performance" && <PerformanceWidget data={data?.performance} period={period} loading={loading} onAskKira={openKira} expanded />}
                {fullWidget === "tickets"     && <TicketsWidget     data={data?.tickets}     period={period} loading={loading} onAskKira={openKira} expanded />}
              </div>
            </div>
          </div>
        )}
      </div>
    </MessengerLayout>
  );
}